/**
 * @fileoverview Workflow automation — frontend metadata (MOR-736)
 *
 * Pure lookup tables shared by the automations manager and the rule-builder
 * dialog: the trigger event catalogue (labels, grouping, which filters make
 * sense per event), the action catalogue (labels, which actions make sense
 * per event), and human-readable summaries for the rules table.
 */

import type {
  AutomationAction,
  AutomationEventType,
  AutomationNotifyTarget,
  AutomationTrigger,
} from "@cvx/automations/automationRules/schema";
import type { BadgeColor } from "@/ui/primitives/badge";

export interface EventOption {
  value: AutomationEventType;
  label: string;
  group: string;
  /** Filters that make sense for this event. */
  filters: ReadonlyArray<
    | "taskTitleContains"
    | "statusKeys"
    | "sendStatuses"
    | "agentIds"
    | "searchKinds"
    | "productKeys"
  >;
  /** Whether the event carries a target the task-centric actions can use. */
  hasTask: boolean;
}

export const EVENT_OPTIONS: ReadonlyArray<EventOption> = [
  // Tasks
  {
    value: "task.created",
    label: "Task created",
    group: "Tasks",
    filters: ["taskTitleContains"],
    hasTask: true,
  },
  {
    value: "task.assigned",
    label: "Task assigned",
    group: "Tasks",
    filters: ["taskTitleContains"],
    hasTask: true,
  },
  {
    value: "task.status_changed",
    label: "Task status changed",
    group: "Tasks",
    filters: ["taskTitleContains", "statusKeys"],
    hasTask: true,
  },
  {
    value: "task.completed",
    label: "Task completed",
    group: "Tasks",
    filters: ["taskTitleContains"],
    hasTask: true,
  },
  // Day one
  {
    value: "day_one.generated",
    label: "Day one documents generated",
    group: "Day one",
    filters: [],
    hasTask: false,
  },
  {
    value: "day_one.status_changed",
    label: "Day one status changed",
    group: "Day one",
    filters: ["statusKeys"],
    hasTask: false,
  },
  {
    value: "day_one.send_status_changed",
    label: "Day one sent / response received",
    group: "Day one",
    filters: ["sendStatuses"],
    hasTask: false,
  },
  // Reports
  {
    value: "report.generated",
    label: "Report generated",
    group: "Reports",
    filters: [],
    hasTask: false,
  },
  {
    value: "report.completed",
    label: "Report marked complete",
    group: "Reports",
    filters: [],
    hasTask: false,
  },
  {
    value: "report.status_changed",
    label: "Report status changed",
    group: "Reports",
    filters: ["statusKeys"],
    hasTask: false,
  },
  // Agents
  {
    value: "agent.completed",
    label: "Data / investigation agent completed",
    group: "Agents",
    filters: ["agentIds"],
    hasTask: false,
  },
  {
    value: "agent.failed",
    label: "Data / investigation agent failed",
    group: "Agents",
    filters: ["agentIds"],
    hasTask: false,
  },
  {
    value: "agent.status_changed",
    label: "Agent status changed",
    group: "Agents",
    filters: ["agentIds", "statusKeys"],
    hasTask: false,
  },
  // Searches
  {
    value: "search.recommended",
    label: "Search recommended",
    group: "Searches",
    filters: ["searchKinds", "productKeys"],
    hasTask: false,
  },
  {
    value: "search.approved",
    label: "Search approved / started",
    group: "Searches",
    filters: ["searchKinds", "productKeys"],
    hasTask: false,
  },
  {
    value: "search.completed",
    label: "Search completed",
    group: "Searches",
    filters: ["searchKinds", "productKeys"],
    hasTask: false,
  },
  {
    value: "search.failed",
    label: "Search failed",
    group: "Searches",
    filters: ["searchKinds", "productKeys"],
    hasTask: false,
  },
];

export const EVENT_BY_VALUE: ReadonlyMap<string, EventOption> = new Map(
  EVENT_OPTIONS.map((e) => [e.value, e]),
);

export type ActionType = AutomationAction["type"];

export interface ActionOption {
  value: ActionType;
  label: string;
  description: string;
  /** Restrict to events where the action has a target; null = any event. */
  allowedEvents: ((event: AutomationEventType) => boolean) | null;
}

const isDayOneEvent = (e: AutomationEventType) => e.startsWith("day_one.");
const isReportEvent = (e: AutomationEventType) => e.startsWith("report.");
const isAgentEvent = (e: AutomationEventType) => e.startsWith("agent.");

export const ACTION_OPTIONS: ReadonlyArray<ActionOption> = [
  {
    value: "create_task",
    label: "Create a task",
    description: "Add a new task on the job this event happened in.",
    allowedEvents: null,
  },
  {
    value: "update_tasks",
    label: "Update task(s)",
    description:
      "Change status, assignee, or priority on the triggering task or tasks matched by title.",
    allowedEvents: null,
  },
  {
    value: "send_notification",
    label: "Send a notification",
    description: "Send an in-app notification to a user.",
    allowedEvents: null,
  },
  {
    value: "start_reminder",
    label: "Start an auto reminder",
    description:
      "Remind someone daily or weekly until a task reaches one of the chosen statuses.",
    allowedEvents: null,
  },
  {
    value: "approve_searches",
    label: "Approve pending searches",
    description:
      "Auto-approve pending registry / KYC search recommendations on the job.",
    allowedEvents: null,
  },
  {
    value: "run_web_search",
    label: "Run AI web search",
    description: "Start an AI web background search on the job.",
    allowedEvents: null,
  },
  {
    value: "set_day_one_status",
    label: "Set day-one document status",
    description: "Apply a firm status to the day-one documents in this event.",
    allowedEvents: isDayOneEvent,
  },
  {
    value: "set_report_status",
    label: "Set report status",
    description: "Apply a firm status to the report in this event.",
    allowedEvents: isReportEvent,
  },
  {
    value: "set_agent_status",
    label: "Set agent status",
    description: "Apply a firm status to the agent in this event.",
    allowedEvents: isAgentEvent,
  },
];

export const ACTION_BY_VALUE: ReadonlyMap<string, ActionOption> = new Map(
  ACTION_OPTIONS.map((a) => [a.value, a]),
);

/** Badge colour per action type — colour-codes action chips in the UI. */
export const ACTION_BADGE_COLOR: Record<ActionType, BadgeColor> = {
  create_task: "green",
  update_tasks: "blue",
  send_notification: "amber",
  start_reminder: "orange",
  approve_searches: "teal",
  run_web_search: "sky",
  set_day_one_status: "indigo",
  set_report_status: "purple",
  set_agent_status: "violet",
};

/** Default (empty) config when the user picks an action type. */
export function defaultActionFor(type: ActionType): AutomationAction {
  switch (type) {
    case "create_task":
      return { type, title: "" };
    case "update_tasks":
      return { type, match: { kind: "trigger_task" } };
    case "send_notification":
      return { type, to: { kind: "task_assignee" }, message: "" };
    case "start_reminder":
      return {
        type,
        frequency: "daily",
        untilStatusKeys: [],
        to: { kind: "task_assignee" },
      };
    case "approve_searches":
      return { type, scope: "determinative" };
    case "run_web_search":
      return { type };
    case "set_day_one_status":
    case "set_report_status":
    case "set_agent_status":
      return { type, statusKey: "" };
  }
}

export const SEND_STATUS_OPTIONS = [
  { value: "unsent", label: "Unsent" },
  { value: "sent", label: "Sent" },
  { value: "response_received", label: "Response received" },
] as const;

export const SEARCH_KIND_OPTIONS = [
  { value: "registry", label: "Registry (InfoTrack)" },
  { value: "kyc", label: "KYC / AML" },
  { value: "web", label: "AI web search" },
] as const;

export const PRIORITY_OPTIONS = [
  { value: 1, label: "Urgent" },
  { value: 2, label: "High" },
  { value: 3, label: "Medium" },
  { value: 4, label: "Low" },
] as const;

export const JOB_STATUS_OPTIONS = [
  { value: "pre_appointment", label: "Pre-appointment" },
  { value: "day_one", label: "Day one" },
  { value: "active", label: "Active" },
  { value: "on_hold", label: "On hold" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
] as const;

export function eventLabel(event: AutomationEventType): string {
  return EVENT_BY_VALUE.get(event)?.label ?? event;
}

/**
 * A trigger summary token: connective/event copy renders as plain text, while
 * concrete values (statuses, send statuses, search types, task titles) render
 * as badges — statuses in their own assigned colour, other values neutral.
 */
export type TriggerToken =
  | { kind: "text"; text: string }
  | { kind: "badge"; label: string; color: BadgeColor };

/** Resolve a task-status key to its display name + assigned colour. */
export type StatusResolver = (
  key: string,
) => { name: string; color?: BadgeColor } | undefined;

/**
 * Structured trigger summary for badge rendering. The event phrase and the
 * connective words stay as text; only value-like elements become badges.
 */
export function triggerTokens(
  trigger: AutomationTrigger,
  resolveStatus?: StatusResolver,
): TriggerToken[] {
  const tokens: TriggerToken[] = [
    { kind: "text", text: eventLabel(trigger.event) },
  ];
  if (trigger.taskTitleContains?.trim()) {
    tokens.push({ kind: "text", text: "· title contains" });
    tokens.push({
      kind: "badge",
      label: trigger.taskTitleContains.trim(),
      color: "gray",
    });
  }
  if (trigger.statusKeys?.length) {
    tokens.push({ kind: "text", text: "· status in" });
    for (const key of trigger.statusKeys) {
      const status = resolveStatus?.(key);
      tokens.push({
        kind: "badge",
        label: status?.name ?? key,
        color: status?.color ?? "gray",
      });
    }
  }
  if (trigger.sendStatuses?.length) {
    tokens.push({ kind: "text", text: "· send status in" });
    for (const value of trigger.sendStatuses) {
      const opt = SEND_STATUS_OPTIONS.find((o) => o.value === value);
      tokens.push({ kind: "badge", label: opt?.label ?? value, color: "gray" });
    }
  }
  if (trigger.searchKinds?.length) {
    tokens.push({ kind: "text", text: "· search type" });
    for (const value of trigger.searchKinds) {
      const opt = SEARCH_KIND_OPTIONS.find((o) => o.value === value);
      tokens.push({ kind: "badge", label: opt?.label ?? value, color: "gray" });
    }
  }
  if (trigger.agentIds?.length) {
    tokens.push({
      kind: "text",
      text: `· ${trigger.agentIds.length} agent(s)`,
    });
  }
  if (trigger.productKeys?.length) {
    tokens.push({
      kind: "text",
      text: `· ${trigger.productKeys.length} product(s)`,
    });
  }
  return tokens;
}

/** One-line "When … if … " summary for the rules table. */
export function describeTrigger(trigger: AutomationTrigger): string {
  const parts: string[] = [eventLabel(trigger.event)];
  if (trigger.taskTitleContains?.trim()) {
    parts.push(`title contains “${trigger.taskTitleContains.trim()}”`);
  }
  if (trigger.statusKeys?.length) {
    parts.push(`status in ${trigger.statusKeys.join(", ")}`);
  }
  if (trigger.sendStatuses?.length) {
    parts.push(`send status in ${trigger.sendStatuses.join(", ")}`);
  }
  if (trigger.agentIds?.length) {
    parts.push(`${trigger.agentIds.length} agent(s)`);
  }
  if (trigger.searchKinds?.length) {
    parts.push(trigger.searchKinds.join(" / "));
  }
  if (trigger.productKeys?.length) {
    parts.push(`${trigger.productKeys.length} product(s)`);
  }
  return parts.join(" · ");
}

/** The rule fields needed to work out which tasks a rule targets. */
export interface RuleForMatching {
  name: string;
  enabled: boolean;
  trigger: AutomationTrigger;
  actions: AutomationAction[];
}

/**
 * Rules that would touch a task with this title — via the trigger's title
 * filter or an action that matches tasks by title. Used to badge tasks in
 * the workflow editor (list + visual) so users can see automation impact.
 */
export function rulesTargetingTaskTitle<T extends RuleForMatching>(
  rules: ReadonlyArray<T>,
  title: string,
): T[] {
  const lower = title.trim().toLowerCase();
  if (!lower) return [];
  return rules.filter((rule) => {
    if (!rule.enabled) return false;
    // A task this rule *creates* is its output, not a task it watches. Skip the
    // trigger-substring match for it so the generating automation doesn't
    // appear on the very task it produced (e.g. trigger "Day Ones" collides
    // with the generated "Review Day Ones" via substring). Action-based matches
    // below still apply if the rule also updates/reminds on this title.
    const createsThisTask = rule.actions.some(
      (a) => a.type === "create_task" && a.title.trim().toLowerCase() === lower,
    );
    const triggerText = rule.trigger.taskTitleContains?.trim().toLowerCase();
    if (!createsThisTask && triggerText && lower.includes(triggerText)) {
      return true;
    }
    return rule.actions.some((a) => {
      if (a.type === "update_tasks") {
        if (a.match.kind === "trigger_task") {
          return (
            !createsThisTask && !!triggerText && lower.includes(triggerText)
          );
        }
        return lower.includes(a.match.text.trim().toLowerCase());
      }
      if (a.type === "start_reminder") {
        const text = a.taskTitleContains?.trim().toLowerCase();
        return !!text && lower.includes(text);
      }
      return false;
    });
  });
}

/** Full one-line "When … → do …" summary of a rule. */
export function describeRule(rule: RuleForMatching): string {
  return `When ${describeTrigger(rule.trigger)} → ${rule.actions
    .map(describeAction)
    .join(" · ")}`;
}

export function describeAction(action: AutomationAction): string {
  switch (action.type) {
    case "create_task":
      return `Create task “${action.title || "…"}”`;
    case "update_tasks":
      return action.match.kind === "trigger_task"
        ? "Update the triggering task"
        : `Update tasks titled “${action.match.text}”`;
    case "send_notification":
      return "Send notification";
    case "start_reminder":
      return `Start ${action.frequency} reminder`;
    case "approve_searches":
      return action.scope === "determinative"
        ? "Approve determinative searches"
        : "Approve all pending searches";
    case "run_web_search":
      return "Run AI web search";
    case "set_day_one_status":
      return `Set day-one status → ${action.statusKey}`;
    case "set_report_status":
      return `Set report status → ${action.statusKey}`;
    case "set_agent_status":
      return `Set agent status → ${action.statusKey}`;
  }
}

// ---------------------------------------------------------------------------
// Plain-language rule summary
//
// Builds a single readable sentence — "When <event>, <do this>." — where only
// value-like elements (statuses, recipients, task titles, priorities) render as
// badges. Statuses keep their assigned colour; everything else is neutral. Used
// by the workflow editor, automations manager and the task side panel so a rule
// reads like a sentence instead of a stack of disconnected badges.
// ---------------------------------------------------------------------------

/** Resolve a user id to a display name (e.g. "Elle Curran"). */
export type UserResolver = (userId: string) => string | undefined;

/** Resolve a workflow role id to its display name + assigned colour. */
export type RoleResolver = (
  roleId: string,
) => { name: string; color?: BadgeColor } | undefined;

function statusToken(
  key: string,
  resolveStatus?: StatusResolver,
): TriggerToken {
  const status = resolveStatus?.(key);
  return {
    kind: "badge",
    label: status?.name ?? key,
    color: status?.color ?? "gray",
  };
}

function userToken(userId: string, resolveUser?: UserResolver): TriggerToken {
  return {
    kind: "badge",
    label: resolveUser?.(userId) ?? "a user",
    color: "gray",
  };
}

/**
 * A workflow role reads as "the job's [Partner]" — the possessive makes it
 * clear the recipient depends on the job rather than being a fixed person.
 */
function roleTokens(
  roleId: string,
  resolveRole?: RoleResolver,
): TriggerToken[] {
  const role = resolveRole?.(roleId);
  return [
    { kind: "text", text: "the job’s" },
    {
      kind: "badge",
      label: role?.name ?? "role holder",
      color: role?.color ?? "gray",
    },
  ];
}

function recipientTokens(
  to: AutomationNotifyTarget,
  resolveUser?: UserResolver,
  resolveRole?: RoleResolver,
): TriggerToken[] {
  switch (to.kind) {
    case "task_assignee":
      return [{ kind: "text", text: "the task assignee" }];
    case "actor":
      return [{ kind: "text", text: "the person who triggered it" }];
    case "user":
      return [userToken(to.userId, resolveUser)];
    case "role":
      return roleTokens(to.roleId, resolveRole);
  }
}

/** Join clause groups with commas + a trailing "and". */
function joinGroups(groups: TriggerToken[][]): TriggerToken[] {
  const out: TriggerToken[] = [];
  groups.forEach((group, i) => {
    if (i > 0) {
      out.push({
        kind: "text",
        text: i === groups.length - 1 ? "and" : ",",
      });
    }
    out.push(...group);
  });
  return out;
}

/** The "When …" clause as flowing text with value badges. */
function triggerSentenceTokens(
  trigger: AutomationTrigger,
  resolveStatus?: StatusResolver,
): TriggerToken[] {
  const tokens: TriggerToken[] = [];
  const text = (t: string) => tokens.push({ kind: "text", text: t });
  const title = trigger.taskTitleContains?.trim();

  const statusChange = () => {
    if (trigger.statusKeys?.length) {
      text("changes to");
      for (const key of trigger.statusKeys) {
        tokens.push(statusToken(key, resolveStatus));
      }
    } else {
      text("changes");
    }
  };

  switch (trigger.event) {
    case "task.created":
    case "task.assigned":
    case "task.completed": {
      const verb =
        trigger.event === "task.created"
          ? "is created"
          : trigger.event === "task.assigned"
            ? "is assigned"
            : "is completed";
      if (title) {
        text("the");
        tokens.push({ kind: "badge", label: title, color: "gray" });
        text(`task ${verb}`);
      } else {
        text(`a task ${verb}`);
      }
      break;
    }
    case "task.status_changed": {
      if (title) {
        text("the");
        tokens.push({ kind: "badge", label: title, color: "gray" });
        text("task’s status");
      } else {
        text("a task’s status");
      }
      statusChange();
      break;
    }
    case "day_one.status_changed":
      text("the day-one status");
      statusChange();
      break;
    case "day_one.send_status_changed":
      text("the day-one send status");
      if (trigger.sendStatuses?.length) {
        text("changes to");
        for (const value of trigger.sendStatuses) {
          const opt = SEND_STATUS_OPTIONS.find((o) => o.value === value);
          tokens.push({
            kind: "badge",
            label: opt?.label ?? value,
            color: "gray",
          });
        }
      } else {
        text("changes");
      }
      break;
    case "day_one.generated":
      text("day-one documents are generated");
      break;
    case "report.generated":
      text("a report is generated");
      break;
    case "report.completed":
      text("a report is marked complete");
      break;
    case "report.status_changed":
      text("the report status");
      statusChange();
      break;
    case "agent.completed":
      text("a data or investigation agent completes");
      break;
    case "agent.failed":
      text("a data or investigation agent fails");
      break;
    case "agent.status_changed":
      text("the agent status");
      statusChange();
      break;
    case "search.recommended":
    case "search.approved":
    case "search.completed":
    case "search.failed": {
      const verb =
        trigger.event === "search.recommended"
          ? "is recommended"
          : trigger.event === "search.approved"
            ? "is approved"
            : trigger.event === "search.completed"
              ? "completes"
              : "fails";
      text(`a search ${verb}`);
      if (trigger.searchKinds?.length) {
        text("for");
        for (const value of trigger.searchKinds) {
          const opt = SEARCH_KIND_OPTIONS.find((o) => o.value === value);
          tokens.push({
            kind: "badge",
            label: opt?.label ?? value,
            color: "gray",
          });
        }
      }
      break;
    }
    default:
      text(eventLabel(trigger.event));
  }
  return tokens;
}

/** A single action clause (e.g. "send a notification to [Elle Curran]"). */
function actionSentenceTokens(
  action: AutomationAction,
  resolveStatus?: StatusResolver,
  resolveUser?: UserResolver,
  resolveRole?: RoleResolver,
): TriggerToken[] {
  const tokens: TriggerToken[] = [];
  const text = (t: string) => tokens.push({ kind: "text", text: t });

  switch (action.type) {
    case "send_notification":
      text("send a notification to");
      tokens.push(...recipientTokens(action.to, resolveUser, resolveRole));
      break;
    case "update_tasks": {
      text("update");
      if (action.match.kind === "trigger_task") {
        text("the triggering task");
      } else {
        text("tasks titled");
        tokens.push({ kind: "badge", label: action.match.text, color: "gray" });
      }
      const sets: TriggerToken[][] = [];
      if (action.setStatusKey) {
        sets.push([
          { kind: "text", text: "set status to" },
          statusToken(action.setStatusKey, resolveStatus),
        ]);
      }
      if (action.setAssigneeRoleId) {
        sets.push([
          { kind: "text", text: "assign to" },
          ...roleTokens(action.setAssigneeRoleId, resolveRole),
        ]);
      } else if (action.setAssigneeUserId) {
        sets.push([
          { kind: "text", text: "assign to" },
          userToken(action.setAssigneeUserId, resolveUser),
        ]);
      }
      if (action.setPriority != null) {
        const p = PRIORITY_OPTIONS.find((o) => o.value === action.setPriority);
        sets.push([
          { kind: "text", text: "set priority to" },
          {
            kind: "badge",
            label: p?.label ?? String(action.setPriority),
            color: "gray",
          },
        ]);
      }
      if (sets.length) {
        text("—");
        tokens.push(...joinGroups(sets));
      }
      break;
    }
    case "create_task":
      text("create a task titled");
      tokens.push({ kind: "badge", label: action.title || "…", color: "gray" });
      if (action.assigneeRoleId) {
        text("assigned to");
        tokens.push(...roleTokens(action.assigneeRoleId, resolveRole));
      } else if (action.assigneeUserId) {
        text("assigned to");
        tokens.push(userToken(action.assigneeUserId, resolveUser));
      }
      if (action.statusKey) {
        text("with status");
        tokens.push(statusToken(action.statusKey, resolveStatus));
      }
      break;
    case "start_reminder":
      text(`send a ${action.frequency} reminder to`);
      tokens.push(...recipientTokens(action.to, resolveUser, resolveRole));
      if (action.untilStatusKeys.length) {
        text("until it reaches");
        for (const key of action.untilStatusKeys) {
          tokens.push(statusToken(key, resolveStatus));
        }
      }
      break;
    case "approve_searches":
      text(
        action.scope === "determinative"
          ? "approve determinative searches"
          : "approve all pending searches",
      );
      break;
    case "run_web_search":
      text("run an AI web search");
      break;
    case "set_day_one_status":
      text("set the day-one status to");
      tokens.push(statusToken(action.statusKey, resolveStatus));
      break;
    case "set_report_status":
      text("set the report status to");
      tokens.push(statusToken(action.statusKey, resolveStatus));
      break;
    case "set_agent_status":
      text("set the agent status to");
      tokens.push(statusToken(action.statusKey, resolveStatus));
      break;
  }
  return tokens;
}

/**
 * Full plain-language summary of a rule as render tokens:
 * "When <trigger>, <action1>, then <action2>."
 */
export function ruleSummaryTokens(
  rule: { trigger: AutomationTrigger; actions: AutomationAction[] },
  resolveStatus?: StatusResolver,
  resolveUser?: UserResolver,
  resolveRole?: RoleResolver,
): TriggerToken[] {
  const tokens: TriggerToken[] = [{ kind: "text", text: "When" }];
  tokens.push(...triggerSentenceTokens(rule.trigger, resolveStatus));

  rule.actions.forEach((action, i) => {
    // Leading comma hugs the previous word; "then" separates further actions.
    tokens.push({ kind: "text", text: i === 0 ? "," : ", then" });
    tokens.push(
      ...actionSentenceTokens(action, resolveStatus, resolveUser, resolveRole),
    );
  });

  if (rule.actions.length > 0) tokens.push({ kind: "text", text: "." });
  return tokens;
}
